import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message } from 'antd';
import { isUnauthorizedRequestError } from '@/utils/requestError';

// 与后端约定的响应数据格式 { code: 0, msg: "", data: {} }
interface ResponseStructure {
  code: number;
  msg: string;
  data: any;
}

// Token 管理
const TOKEN_KEY = 'admin_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string) => {
  localStorage.setItem(TOKEN_KEY, token);
};
export const removeToken = () => localStorage.removeItem(TOKEN_KEY);

/**
 * @name 错误处理
 * 适配后端 Go API 返回格式: { code: 0, msg: "", data: {} }
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  baseURL:
    process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8090',

  // 错误处理
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { code, msg, data } = res as unknown as ResponseStructure;
      // code 不为 0 表示业务错误
      if (code !== 0) {
        const error: any = new Error(msg);
        error.name = 'BizError';
        error.info = { code, msg, data };
        throw error;
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 业务错误
      if (error.name === 'BizError') {
        const errorInfo = error.info;
        if (errorInfo) {
          const { msg } = errorInfo;
          // 401 未授权，跳转登录
          if (isUnauthorizedRequestError(error)) {
            removeToken();
            history.push('/user/login');
          }
          message.error(msg || '请求失败');
        }
      } else if (error.response) {
        // HTTP 错误
        const { status, data } = error.response;
        const responseMessage =
          typeof data?.msg === 'string' ? data.msg.trim() : '';
        const isLoginRequest = error.config?.url?.endsWith('/admin/auth/login');
        if (isUnauthorizedRequestError(error) && !isLoginRequest) {
          removeToken();
          history.push('/user/login');
        }
        message.error(responseMessage || `请求失败（${status}）`);
      } else if (error.request) {
        message.error('网络错误，请检查网络连接');
      } else {
        message.error('请求配置错误');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 添加 Token 到请求头
      const token = getToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: token,
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as any;

      // 后端返回格式: { code: 0, msg: "", data: {} }
      // 如果 code 不为 0，说明有错误
      if (data && data.code !== undefined && data.code !== 0) {
        // 这里会被 errorThrower 捕获
      }

      return response;
    },
  ],
};
